/**
 * Presentation logic for the Business Unit Groups screen (f4.1).
 *
 * Everything here is pure, so the rules that shape the table are testable
 * without rendering anything. Nothing in this file decides what a caller may
 * see or do: visibility is the server's answer, and so is the status label.
 */

import type { ActiveChildren, BusinessUnitGroup } from '@/api/client';
import { ApiError } from '@/api/client';
import { sortRows, type SortState, type SortValue } from '@/ui/tableSort';

import { ownerFor } from './ownershipView';

/** The status codes the screen styles. Anything else falls back to neutral. */
const ACTIVE = 'ACTIVE';

/**
 * The badge class for a status.
 *
 * Only the CLASS is derived from the code -- the LABEL always comes from the
 * server's `status.name`, so a status renamed in the lookup table renames
 * itself here with no frontend change (f1.3's rule, restated by f2.2).
 */
export function badgeClassFor(statusCode: string): string {
  return statusCode === ACTIVE ? 'badge pill-success' : 'badge pill-neutral';
}

export function isActive(group: BusinessUnitGroup): boolean {
  return group.status.code === ACTIVE;
}

/**
 * Which column the table is sorted by. EVERY column the table renders is
 * here except the one holding the row actions, which has nothing to order
 * by. All of them run client-side over the set the server returned whole.
 */
export type SortColumn = 'code' | 'name' | 'units' | 'DEM' | 'CEM' | 'status';

export interface TableView {
  search: string;
  sort: SortState<SortColumn>;
}

/** The default order: by code, ascending. */
export const DEFAULT_SORT: SortState<SortColumn> = { column: 'code', direction: 'asc' };

/**
 * What a column sorts by for one group.
 *
 * The owner columns sort by the holder's display name and the status column
 * by the label the SERVER gave it, so the order on screen matches the order
 * the reader sees rather than an id nobody can see.
 */
export function sortValueOf(group: BusinessUnitGroup, column: SortColumn): SortValue {
  switch (column) {
    case 'code':
      return group.code;
    case 'name':
      return group.name;
    case 'status':
      return group.status.name;
    case 'units':
      // f5.1. A number, so it sorts numerically rather than as "10" < "2".
      return group.business_unit_count;
    default:
      return ownerFor(group, column)?.display_name ?? null;
  }
}

/**
 * Search across code and name, case-insensitively.
 *
 * Client-side over the loaded set, as f2.2's mapping tab and f3.1's roster
 * both do: the server takes no `q` parameter, because six rows do not need
 * one and a query parameter nobody needs is an endpoint nobody can remove.
 */
export function applySearch(
  groups: readonly BusinessUnitGroup[],
  search: string,
): BusinessUnitGroup[] {
  const needle = search.trim().toLowerCase();
  if (!needle) return [...groups];
  return groups.filter(
    (group) =>
      group.code.toLowerCase().includes(needle) ||
      group.name.toLowerCase().includes(needle),
  );
}

export function applySort(
  groups: readonly BusinessUnitGroup[],
  sort: SortState<SortColumn>,
): BusinessUnitGroup[] {
  return sortRows(groups, sort, sortValueOf);
}

export function visibleGroups(
  groups: readonly BusinessUnitGroup[],
  view: TableView,
): BusinessUnitGroup[] {
  return applySort(applySearch(groups, view.search), view.sort);
}

/**
 * Re-exported, not defined here.
 *
 * Reading f2.1's error envelope is the whole API's concern rather than this
 * screen's, so it lives in `@/api/fieldErrors` now that the Users screen needs
 * it too. This screen keeps importing it from its own view module, which is
 * where every other presentation helper it uses already is.
 */
export { generalMessage, problemFor } from '@/api/fieldErrors';

/**
 * The children a refused deactivation named, or null.
 *
 * The client never computes this: it issues the deactivation WITHOUT
 * acknowledgement first and renders whatever the server refused with, so the
 * warning cannot disagree with the rule the server enforces.
 */
export function activeChildrenFrom(error: unknown): ActiveChildren | null {
  return error instanceof ApiError ? error.activeChildren : null;
}

/** Whether a failure is a refusal by policy rather than something to retry. */
export function isRefusal(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403;
}

/**
 * A provenance timestamp, or a placeholder.
 *
 * The server stores all four values on the row itself and always sends them,
 * so this placeholder is a guard rather than a state the screen expects -- the
 * wire type is nullable and a blank cell reading like "nobody has touched
 * this" would be worse than saying nothing is recorded.
 *
 * Note it is NOT the same thing as a write nobody was behind: a migration seed
 * or the import job sends `System`, which is a real answer and renders as one.
 */
export const PROVENANCE_MISSING = 'not recorded';

export function formatMoment(value: string | null | undefined): string {
  if (!value) return PROVENANCE_MISSING;
  const moment = new Date(value);
  if (Number.isNaN(moment.getTime())) return PROVENANCE_MISSING;
  return moment.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export function formatActor(actor: { display: string } | null | undefined): string {
  return actor?.display ?? PROVENANCE_MISSING;
}

/** Two letters for the avatar, from whatever the server called them. */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
